import { ArrowLeft, ChevronDown, ChevronRight, PanelRightClose } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

export interface SessionContextViewOption {
  value: string;
  label: string;
}

interface SessionContextDetailHeaderProps {
  entityName: string;
  onBack: () => void;
  onCollapse: () => void;
  views: SessionContextViewOption[];
  currentView: string;
  onSetView: (view: string) => void;
}

export const SessionContextDetailHeader = ({
  entityName,
  onBack,
  onCollapse,
  views,
  currentView,
  onSetView,
}: SessionContextDetailHeaderProps) => {
  const current = views.find((v) => v.value === currentView) ?? views[0];

  return (
    <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
      <Button
        variant="ghost"
        size="icon"
        onClick={onBack}
        aria-label="Back to session context"
        className="size-8"
      >
        <ArrowLeft className="size-4" />
      </Button>

      <span
        className="min-w-0 max-w-[45%] shrink-0 truncate text-sm font-medium text-muted-foreground"
        title={entityName}
      >
        {entityName}
      </span>

      <ChevronRight className="size-4 shrink-0 text-muted-foreground/50" aria-hidden="true" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 text-sm font-medium text-foreground hover:bg-accent"
          >
            {current.label}
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {views.map((v) => (
            <DropdownMenuItem
              key={v.value}
              onClick={() => onSetView(v.value)}
              className={cn(v.value === currentView && "bg-accent font-medium text-primary")}
            >
              {v.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Tooltip delayDuration={700}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCollapse}
            aria-label="Collapse session context"
            className="ml-auto size-8"
          >
            <PanelRightClose className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Toggle Context</TooltipContent>
      </Tooltip>
    </div>
  );
};
